import {
  Chain,
  Company,
  Image,
  TConfiguration,
  useSearch,
} from '@nettbureau/featured-core';
import { TranslationProvider } from '@nettbureau/featured-translations';
import { Provider } from '@nettbureau/vibrant-ui';
import { useTranslate } from '@tolgee/react';
import { PageProps } from 'gatsby';
import { Fragment } from 'react';
import { ContactCard } from '../card/ContactCard';
import { ClientSideRender } from '../ClientSideRender';
import { ContactLink } from '../ContactLink';
import {
  ContainerWithTheme,
  FeaturedContainer,
} from '../container/FeaturedContainer';
import { ConfigProvider } from '../provider/config-provider';
import { BasicCompaniesSection } from '../sections/BasicCompaniesSection';
import { Header } from '../virtualized-grid/Header';
import { VirtualizedGrid } from '../virtualized-grid/VirtualizedGrid';

export default function Page(props: PageProps<object, VendorListProps>) {
  return (
    <ClientSideRender>
      <Provider background={null}>
        <TranslationProvider language={props.pageContext.language}>
          <Render {...props.pageContext}></Render>
        </TranslationProvider>
      </Provider>
    </ClientSideRender>
  );
}

export interface VendorListProps {
  language: string;
  hideTitle: boolean;
  companies: (Company | Chain)[];
  deadCompanies: Company[];
  title: string;
  contactUrl: string | null;
  defaultBackground: Image | null;
  catalogConfiguration: TConfiguration;
}

function Render(props: VendorListProps) {
  const { t } = useTranslate();

  const {
    defaultBackground,
    catalogConfiguration,
    title,
    companies,
    contactUrl,
    deadCompanies,
    hideTitle,
  } = props;

  const { items, numItems, query, setQuery } = useSearch({
    collection: companies,
    attributes: ['name', 'address', 'postcode', 'postarea'],
  });

  return (
    <Fragment>
      <ConfigProvider
        value={{
          globalThemeVariant: 'vendorListPage',
          ...catalogConfiguration,
        }}
      >
        <ContainerWithTheme>
          <FeaturedContainer>
            <Header
              title={hideTitle ? null : title}
              resultsTitle={t('results')}
              setQuery={setQuery}
              query={query}
              numResults={numItems}
            />

            <VirtualizedGrid
              items={items}
              query={query}
              setQuery={setQuery}
              itemRenderer={(attributes, index) => {
                const {
                  subscriptionLevel,
                  logo,
                  background,
                  name,
                  postarea,
                  address,
                  postcode,
                  internalLink,
                  email,
                  phone,
                  website,
                } = attributes;

                return (
                  <ContactCard
                    minHeight={130}
                    id={attributes.id}
                    key={index}
                    center={null}
                    subTitle={null}
                    subscriptionLevel={subscriptionLevel}
                    title={name}
                    icon={'icon' in attributes ? attributes.icon : null}
                    bodyLine1={address ?? null}
                    bodyLine2={`${postcode ?? ''} ${postarea ?? ''}`}
                    background={background ?? defaultBackground}
                    logo={logo}
                    website={website}
                    phone={phone}
                    email={email}
                    href={internalLink}
                  />
                );
              }}
            />
            {contactUrl && <ContactLink color="#000" url={contactUrl} />}
          </FeaturedContainer>
        </ContainerWithTheme>
      </ConfigProvider>

      {deadCompanies.length > 0 && (
        <FeaturedContainer>
          <BasicCompaniesSection
            deadCompanies={deadCompanies}
            nonClientCompanies={[]}
            companies={[]}
          />
        </FeaturedContainer>
      )}
    </Fragment>
  );
}
